<template>
  <v-card flat class="bill-detail">
    <v-row class="d-flex">
      <!-- Order Info Card -->
      <v-col cols="12" md="6" class="d-flex">
        <v-card
          outlined
          class="pa-4 mb-4 rounded-lg d-flex flex-column"
          style="width: 100%"
        >
          <v-card-title class="text-subtitle-1 font-weight-bold">
            <v-icon small color="primary" left>fas fa-shopping-cart</v-icon>

            {{ $t("order_details") }}

            <v-spacer />
            <v-chip
              small
              label
              dark
              class="rounded-lg"
              :color="orderStatusColor(item.status)"
            >
              {{ $t(item.status) }}
            </v-chip>
          </v-card-title>
          <v-divider class="mt-0" />

          <v-row dense>
            <v-col cols="6" class="text-caption text--secondary">
              {{ $t("code") }}
            </v-col>
            <v-col cols="6" class="text-right">
              <span class="font-weight-medium">{{ item.code }}</span>
              <v-btn
                icon
                x-small
                @click="copyToClipboard(item.code)"
                class="ml-1"
              >
                <v-icon small>far fa-copy</v-icon>
              </v-btn>
            </v-col>

            <template v-if="queueLabel">
              <v-col cols="6" class="text-caption text--secondary">
                {{ $t("queue_number") }}
              </v-col>
              <v-col cols="6" class="text-right">
                <span class="text-h6 font-weight-bold primary--text">
                  {{ queueLabel }}
                </span>
              </v-col>
            </template>

            <v-col
              v-if="item.user_id"
              cols="6"
              class="text-caption text--secondary"
            >
              {{ $t("seller") }}
            </v-col>
            <v-col cols="6" class="text-right">
              {{ item.user_id.first_name }}
            </v-col>

            <v-col cols="6" class="text-caption text--secondary">
              {{ $t("product_item") }}
            </v-col>
            <v-col cols="6" class="text-right">
              {{ item.history_count | ToNumber }} {{ $t("item") }}
            </v-col>
            <v-col
              cols="6"
              class="text-caption text--secondary"
              v-if="item.table_id"
            >
              {{ $t("table") }}
            </v-col>
            <v-col cols="6" class="text-right" v-if="item.table_id">
              {{ item.table_id.name }}
            </v-col>
            <template v-if="hasBuffetPlans">
              <v-col cols="6" class="text-caption text--secondary">
                {{ $t("buffet_persons") }}
              </v-col>
              <v-col cols="6" class="text-right">
                <span class="font-weight-medium">{{ buffetTotalPersons }}</span>
              </v-col>
            </template>
            <v-col cols="6" class="text-caption text--secondary">
              {{ $t("sale_date") }}
            </v-col>
            <v-col cols="6" class="text-right">
              {{ item.created_at | moment }}
            </v-col>
          </v-row>
        </v-card>
      </v-col>

      <!-- Payment Info Card -->
      <v-col cols="12" md="6" class="d-flex">
        <v-card
          outlined
          class="pa-4 mb-4 rounded-lg d-flex flex-column"
          style="width: 100%"
        >
          <v-card-title class="text-subtitle-1 font-weight-bold">
            <v-icon color="info" left small>fas fa-credit-card</v-icon>

            {{ $t("payment_details") }}
            <v-spacer />
            <v-chip small label outlined class="rounded-lg">
              {{ $t(item.type) }}
            </v-chip>
          </v-card-title>
          <v-divider class="mt-0" />

          <v-row dense>
            <!-- grand_total -->
            <v-col cols="6" class="text-caption text--secondary">
              {{ $t("product_price") }}
            </v-col>
            <v-col cols="6" class="text-right font-weight-medium">
              {{ grand_total }} {{ productCurrency }}
            </v-col>
            <!-- discount -->
            <v-col
              cols="6"
              v-if="item.total_discount"
              class="text-caption text--secondary"
            >
              {{ $t("discount") }}
            </v-col>
            <v-col
              cols="6"
              v-if="item.total_discount"
              class="text-right font-weight-medium red--text"
            >
              {{ total_discount }}
            </v-col>

            <!-- Tax amount -->
            <template v-if="shouldShowTax">
              <v-col cols="6" class="text-caption text--secondary">
                {{ $t("tax") }} ({{ taxPercentage }}%)
              </v-col>
              <v-col cols="6" class="text-right font-weight-medium">
                +{{ tax_amount_formatted }} {{ productCurrency }}
              </v-col>
            </template>

            <!-- Grand Total before payment (hidden when price THB / paid LAK – we show exchange + amount paid only) -->
            <template v-if="!isPaymentInDifferentCurrency">
              <v-col cols="6" class="text-subtitle-2">
                {{ $t("grand_total") }}
              </v-col>
              <v-col
                cols="6"
                class="text-right text-subtitle-2"
                :class="
                  isExactPayment
                    ? 'font-weight-bold primary--text'
                    : 'font-weight-bold'
                "
              >
                {{ grandTotalBeforePayment }} {{ productCurrency }}
              </v-col>
            </template>

            <!-- When price is in THB (or other) and user paid in LAK: show exchange rate and amount paid (LAK) only -->
            <template v-if="isPaymentInDifferentCurrency">
              <v-col cols="6" class="text-caption text--secondary">
                {{ $t("exchange_rate") }}
              </v-col>
              <v-col cols="6" class="text-right font-weight-medium">
                1 {{ productCurrency }} =
                {{ item.exchange_rate | NumberFormat }}
                {{ paymentCurrency }}
              </v-col>
              <v-col cols="6" class="text-subtitle-1 font-weight-bold">
                {{ $t("amount_paid") }} ({{ paymentCurrency }})
              </v-col>
              <v-col
                cols="6"
                class="text-right text-subtitle-1 font-weight-bold primary--text"
              >
                {{ total_paid_formatted }} {{ paymentCurrency }}
              </v-col>
            </template>

            <!-- When same currency: show grand total + amount paid only when different from grand total -->
            <template v-else-if="!isExactPayment">
              <v-col cols="6" class="text-subtitle-1 font-weight-bold">
                {{ $t("amount_paid") }}
              </v-col>
              <v-col
                cols="6"
                class="text-right text-subtitle-1 font-weight-bold primary--text"
              >
                {{ total_paid_formatted }} {{ paymentCurrency }}
              </v-col>
            </template>

            <!-- Change section - show when paid more than total -->
            <template v-if="hasChange">
              <v-col cols="6" class="text-subtitle-2">
                {{ $t("change") }}
              </v-col>
              <v-col
                cols="6"
                class="text-right text-subtitle-2 font-weight-bold warning--text"
              >
                {{ change_formatted }} {{ paymentCurrency }}
              </v-col>
            </template>

            <!-- Cash + transfer split (cash_transfer tickets) -->
            <template v-if="cashTransferBreakdown">
              <v-col cols="6" class="text-caption text--secondary">
                {{ $t("cash_amount") }}
              </v-col>
              <v-col cols="6" class="text-right font-weight-medium">
                {{ cashTransferBreakdown.cash | NumberFormat }}
                {{ paymentCurrency }}
              </v-col>
              <v-col cols="6" class="text-caption text--secondary">
                {{ $t("transfer_amount") }}
              </v-col>
              <v-col cols="6" class="text-right font-weight-medium">
                {{ cashTransferBreakdown.transfer | NumberFormat }}
                {{ paymentCurrency }}
              </v-col>
            </template>

            <v-col cols="6" class="text-caption text--secondary">
              {{ $t("profit") }}
            </v-col>
            <v-col cols="6" class="text-right d-flex text-right justify-end">
              <span
                v-if="checkPermission('show_profit')"
                class="font-weight-medium"
                :class="item.total_profit > 0 ? 'success--text' : 'error--text'"
              >
                {{ item.total_profit | NumberFormat }}
                {{ productCurrency }}
              </span>
              <span v-else class="font-weight-medium grey--text">N/A</span>
              <v-icon
                v-if="checkPermission('show_profit')"
                small
                :color="item.total_profit > 0 ? 'success' : 'error'"
                class="ml-1"
              >
                {{
                  item.total_profit > 0
                    ? "fas fa-arrow-up"
                    : "fas fa-arrow-down"
                }}
              </v-icon>
            </v-col>
          </v-row>
        </v-card>
      </v-col>
    </v-row>

    <!-- Customer Info Section -->
    <v-card outlined class="mb-4 rounded-lg" v-if="isCustomer">
      <v-card-title class="text-subtitle-1 font-weight-bold">
        <v-icon small color="success" left>fas fa-user</v-icon>
        {{ $t("customer_information") }}
      </v-card-title>
      <v-divider class="mt-0" />

      <v-row class="pa-4">
        <!-- Personal Information -->
        <v-col cols="12" md="6">
          <div class="text-subtitle-2 font-weight-medium mb-3 text-success">
            <v-icon small color="success" left>fas fa-id-card</v-icon>
            {{ $t("personal_information") }}
          </div>

          <v-row dense>
            <v-col cols="6" class="text-caption text--secondary">
              {{ $t("customer_name") }}
            </v-col>
            <v-col cols="6" class="text-right">
              <span class="font-weight-medium">{{
                item.customer_id.first_name
              }}</span>
            </v-col>

            <v-col cols="6" class="text-caption text--secondary">
              {{ $t("phone_number") }}
            </v-col>
            <v-col cols="6" class="text-right">
              <span class="font-weight-medium">{{
                item.customer_id.phone_no
              }}</span>
            </v-col>

            <v-col cols="6" class="text-caption text--secondary">
              {{ $t("loyalty_points") }}
            </v-col>
            <v-col cols="6" class="text-right">
              <v-chip small color="warning" outlined>
                {{ item.customer_id.point }} {{ $t("point") }}
              </v-chip>
            </v-col>
          </v-row>
        </v-col>

        <!-- Address Information -->
        <v-col cols="12" md="6">
          <div class="text-subtitle-2 font-weight-medium mb-3 text-info">
            <v-icon small color="info" left>fas fa-map-marker-alt</v-icon>
            {{ $t("delivery_address") }}
          </div>

          <v-row dense>
            <v-col cols="6" class="text-caption text--secondary">
              {{ $t("village") }}
            </v-col>
            <v-col cols="6" class="text-right">
              <span class="font-weight-medium">{{
                item.customer_id.village?.vill_name || $t("not_specified")
              }}</span>
            </v-col>

            <v-col cols="6" class="text-caption text--secondary">
              {{ $t("district") }}
            </v-col>
            <v-col cols="6" class="text-right">
              <span class="font-weight-medium">{{
                item.customer_id.village?.district?.dr_name ||
                $t("not_specified")
              }}</span>
            </v-col>

            <v-col cols="6" class="text-caption text--secondary">
              {{ $t("province") }}
            </v-col>
            <v-col cols="6" class="text-right">
              <span class="font-weight-medium">{{
                item.customer_id.village?.district?.province?.pr_name ||
                $t("not_specified")
              }}</span>
            </v-col>
          </v-row>
        </v-col>

        <!-- Logistics Information -->
        <v-col cols="12" v-if="item.customer_id.logistics">
          <v-divider class="my-3" />
          <div class="text-subtitle-2 font-weight-medium mb-3 text-primary">
            <v-icon small color="primary" left>fas fa-truck</v-icon>
            {{ $t("logistics_partner") }}
          </div>

          <v-row align="center">
            <v-col cols="auto">
              <v-avatar size="40" class="mr-3">
                <v-img
                  :src="item.customer_id.logistics.logo"
                  :alt="item.customer_id.logistics.name"
                  @error="handleImageError"
                />
              </v-avatar>
            </v-col>
            <v-col>
              <div class="text-subtitle-2 font-weight-medium">
                {{ item.customer_id.logistics.name }}
              </div>
              <div class="text-caption grey--text">
                {{ $t("delivery_partner") }}
              </div>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </v-card>

    <!-- Buffet plans (same payload as receipt / ticket detail API) -->
    <v-card v-if="hasBuffetPlans" outlined class="mb-4 rounded-lg">
      <v-card-title class="text-subtitle-1 font-weight-bold">
        <v-icon small color="deep-purple darken-1" left>fal fa-utensils</v-icon>
        {{ $t("buffet_plans") }}
        <v-spacer />
        <v-chip small outlined color="deep-purple" class="rounded-lg">
          {{ $t("buffet_persons") }}: {{ buffetTotalPersons }}
        </v-chip>
      </v-card-title>
      <v-divider class="mt-0" />
      <v-simple-table dense class="bill-report-buffet-table">
        <template v-slot:default>
          <thead>
            <tr>
              <th class="text-left text-caption">
                {{ $t("buffet_plan_name") }}
              </th>
              <th class="text-center text-caption">
                {{ $t("buffet_persons") }}
              </th>
              <th class="text-right text-caption">
                {{ $t("buffet_plan_price") }}
              </th>
              <th class="text-right text-caption">{{ $t("total") }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(plan, idx) in receiptBuffetPlans" :key="'bp-' + idx">
              <td class="font-weight-medium">{{ plan.plan_name }}</td>
              <td class="text-center">{{ plan.persons | ToNumber }}</td>
              <td class="text-right">
                {{ plan.unit_price | NumberFormat }} {{ productCurrency }}
              </td>
              <td class="text-right font-weight-medium">
                {{ plan.subtotal | NumberFormat }} {{ productCurrency }}
              </td>
            </tr>
          </tbody>
        </template>
      </v-simple-table>
    </v-card>

    <!-- Products List with Expansion Panels -->
    <v-card outlined class="bill-detail-products mb-4 rounded-lg">
      <v-card-title class="text-subtitle-1 font-weight-bold">
        {{ $t("products") }}
      </v-card-title>
      <v-divider class="mt-0" />

      <v-expansion-panels accordion flat>
        <v-expansion-panel
          v-for="(product, index) in item.history"
          :key="index"
          class="mb-2"
          :class="
            index === item.history.length - 1
              ? 'border-bottom-0'
              : 'border-bottom'
          "
        >
          <v-expansion-panel-header>
            <v-row align="center" class="mx-0">
              <v-col cols="1">
                {{ index + 1 }}
              </v-col>
              <v-col cols="1" class="pa-0">
                <app-img :img="product.product.img" width="40" height="40" />
              </v-col>
              <v-col cols="4" class="pa-0">
                <div
                  class="text-subtitle-2 font-weight-medium d-flex align-center flex-wrap"
                >
                  <span class="mr-2">{{ product.product.name }}</span>
                  <v-chip
                    v-if="isStampRewardLine(item, product)"
                    x-small
                    color="amber darken-2"
                    text-color="white"
                    class="font-weight-bold"
                  >
                    <v-icon left x-small>fal fa-stamp</v-icon>
                    {{ $t("free") }} · {{ $t("stamp_paid_by_stamp") }}
                  </v-chip>
                </div>
                <div class="text-caption grey--text">
                  {{ product.product.code }}
                </div>
                <!-- Show variant label if present -->
                <div v-if="product.product_variant">
                  <v-chip x-small color="primary" outlined class="mt-1">
                    {{ product.product_variant.sku }}
                  </v-chip>
                </div>
              </v-col>
              <v-col cols="2" class="pa-0 text-center">
                <div class="text-subtitle-2">
                  <template v-if="isStampRewardLine(item, product)">
                    {{ $t("free") }}
                  </template>
                  <template v-else>
                    {{ product.price_sale | NumberFormat }}
                  </template>
                </div>
                <div class="text-caption grey--text">
                  {{ $t("price") }} ({{
                    product.product.currency?.code || "LAK"
                  }})
                </div>
              </v-col>
              <v-col cols="1" class="pa-0 text-center">
                <div class="text-subtitle-2">
                  {{ product.qty | ToNumber }}
                </div>
                <div class="text-caption grey--text">
                  {{ $t("qty") }}
                </div>
              </v-col>
              <v-col cols="2" class="pa-0 text-center">
                <div class="text-subtitle-2 font-weight-bold">
                  <template v-if="isStampRewardLine(item, product)">
                    {{ $t("free") }}
                  </template>
                  <template v-else>
                    {{ product.total_price | NumberFormat }}
                  </template>
                </div>
                <div class="text-caption grey--text">
                  {{ $t("total") }} ({{
                    product.product.currency?.code || "LAK"
                  }})
                </div>
              </v-col>
            </v-row>
          </v-expansion-panel-header>

          <v-expansion-panel-content>
            <v-alert
              v-if="isStampRewardLine(item, product)"
              type="info"
              dense
              outlined
              class="mb-3 mt-2"
              icon="fal fa-stamp"
            >
              {{ $t("stamp_paid_by_stamp") }}
            </v-alert>
            <!-- Stock Information -->
            <v-divider class="mb-3 mt-3" />
            <div class="text-subtitle-2 font-weight-medium mb-2">
              {{ $t("stock_info") }}
            </div>
            <v-divider class="mb-3" />
            <!-- Product Details -->
            <v-row class="mb-3">
              <v-col cols="6">
                <div class="text-caption grey--text">{{ $t("category") }}</div>
                <div class="text-body-2">
                  {{
                    product.product.category?.la_name ||
                    product.product.category?.en_name ||
                    $t("no_category")
                  }}
                </div>
              </v-col>
              <v-col cols="6">
                <div class="text-caption grey--text">{{ $t("currency") }}</div>
                <div class="text-body-2">
                  {{ product.product.currency?.code || "LAK" }}
                </div>
              </v-col>
              <v-col cols="6">
                <div class="text-caption grey--text">{{ $t("unit") }}</div>
                <div class="text-body-2">{{ getProductUnit(product) }}</div>
              </v-col>
              <v-col cols="6">
                <div class="text-caption grey--text">{{ $t("supplier") }}</div>
                <div class="text-body-2">{{ getProductSupplier(product) }}</div>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="3" v-if="isStockTrackingEnabled(product)">
                <div class="text-caption grey--text">{{ $t("pre_stock") }}</div>
                <div class="text-body-2">{{ product.pre_stock }}</div>
              </v-col>
              <v-col cols="3" v-if="isStockTrackingEnabled(product)">
                <div class="text-caption grey--text">
                  {{ $t("post_stock") }}
                </div>
                <div class="text-body-2">{{ product.post_stock }}</div>
              </v-col>
              <v-col cols="3" v-if="!isStockTrackingEnabled(product)">
                <div class="text-caption grey--text">
                  {{ $t("stock_tracking") }}
                </div>
                <div class="text-body-2">{{ $t("disabled") }}</div>
              </v-col>
              <v-col cols="3">
                <div class="text-caption grey--text">
                  {{ $t("exchange_rate") }}
                </div>
                <div class="text-body-2">{{ product.exchange_rate }}</div>
              </v-col>
            </v-row>
            <!-- Price Details -->
            <v-divider class="mb-3 mt-3" />
            <div class="text-subtitle-2 font-weight-medium mb-2">
              {{ $t("price_details") }}
            </div>
            <v-row>
              <v-col cols="4">
                <div class="text-caption grey--text">{{ $t("buy_price") }}</div>
                <div class="text-body-2">
                  <span v-if="checkPermission('show_cost')">
                    {{ product.price_buy | NumberFormat }}
                    {{ product.product.currency?.code || "LAK" }}
                  </span>
                  <span v-else class="grey--text">N/A</span>
                </div>
              </v-col>
              <v-col cols="4">
                <div class="text-caption grey--text">
                  {{ $t("sale_price") }}
                </div>
                <div class="text-body-2 font-weight-medium">
                  {{ product.price_sale | NumberFormat }}
                  {{ product.product.currency?.code || "LAK" }}
                </div>
              </v-col>
              <v-col cols="4">
                <div class="text-caption grey--text">{{ $t("profit") }}</div>
                <div
                  v-if="checkPermission('show_profit')"
                  class="text-body-2"
                  :class="getProfitColor(product)"
                >
                  {{ getProfitAmount(product) | NumberFormat }}
                  {{ product.product.currency?.code || "LAK" }}
                </div>
                <div v-else class="text-body-2 grey--text">N/A</div>
              </v-col>
            </v-row>
            <!-- Variant Details -->
            <div v-if="product.product_variant">
              <v-divider class="mb-3" />
              <div class="text-subtitle-2 font-weight-medium mb-2">
                {{ $t("variant_details") }}
              </div>
              <v-row>
                <v-col cols="4">
                  <div class="text-caption grey--text">{{ $t("sku") }}</div>
                  <div class="text-body-2">
                    {{ product.product_variant.sku }}
                  </div>
                </v-col>
                <v-col cols="4">
                  <div class="text-caption grey--text">
                    {{ $t("variant_price") }}
                  </div>
                  <div class="text-body-2">
                    {{ product.product_variant.price | NumberFormat }}
                  </div>
                </v-col>
                <v-col cols="4">
                  <div class="text-caption grey--text">{{ $t("options") }}</div>
                  <div class="text-body-2">
                    <span
                      v-for="(val, key) in getVariantOptions(
                        product.product_variant,
                      )"
                      :key="key"
                    >
                      <v-chip x-small color="secondary" outlined class="mr-1"
                        >{{ key }}: {{ val }}</v-chip
                      >
                    </span>
                  </div>
                </v-col>
              </v-row>
            </div>

            <!-- Bundle Stock Information -->
            <div
              v-if="
                product &&
                product.product.is_bundle &&
                product.bundle_stock_logs &&
                product.bundle_stock_logs.length > 0
              "
            >
              <v-divider class="mb-3" />
              <div class="text-subtitle-2 font-weight-medium mb-2">
                {{ $t("bundle_stock_info") }}
              </div>
              <v-simple-table dense>
                <thead>
                  <tr>
                    <th class="text-left">{{ $t("product") }}</th>
                    <th class="text-center">{{ $t("pre_stock") }}</th>
                    <th class="text-center">{{ $t("amount") }}</th>
                    <th class="text-center">{{ $t("post_stock") }}</th>
                    <th class="text-center">{{ $t("current_stock") }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(stockLog, logIndex) in product.bundle_stock_logs"
                    :key="logIndex"
                  >
                    <td>
                      <div class="d-flex align-center">
                        <v-avatar size="30" class="mr-2">
                          <app-img
                            :img="
                              stockLog.bundle_item_product?.img ||
                              '/images/no_photo.jpg'
                            "
                            width="24"
                            height="24"
                          />
                        </v-avatar>
                        <div>
                          <div class="text-body-2 font-weight-medium">
                            {{ stockLog.bundle_item_product?.name }}
                          </div>
                          <div class="text-caption grey--text">
                            {{ stockLog.bundle_item_product?.code }}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td class="text-center">
                      {{ stockLog.pre_stock | ToNumber }}
                    </td>
                    <td class="text-center">
                      {{ stockLog.bundle_qty | ToNumber }}
                    </td>
                    <td class="text-center">
                      {{ stockLog.post_stock | ToNumber }}
                    </td>
                    <td class="text-center">
                      {{ stockLog.bundle_item_product.stock | ToNumber }}
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
            </div>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-card>

    <!-- Cancelled items, one entry per cancel (QRIPO-543) -->
    <v-card outlined class="rounded-lg mb-4" v-if="hasCancellations">
      <v-card-text>
        <CancelHistory :cancellations="item.cancellations" />
      </v-card-text>
    </v-card>

    <!-- Product Return Section (bills cancelled before the cancel history existed) -->
    <v-card
      outlined
      class="rounded-lg mb-4"
      v-if="item.status === 'cancel' && item.product_return && !hasCancellations"
    >
      <v-card-title class="text-subtitle-1 font-weight-bold">
        <v-icon small color="error" left>fas fa-undo</v-icon>
        {{ $t("product_return") }}
      </v-card-title>
      <v-card-text class="text-body-2">
        <v-row dense>
          <v-col cols="6" class="text-caption text--secondary">
            {{ $t("con_fund") }}
          </v-col>
          <v-col cols="6" class="text-right font-weight-medium">
            {{ $t(item.product_return.return_type) }}
          </v-col>

          <v-col cols="6" class="text-caption text--secondary">
            {{ $t("noted") }}
          </v-col>
          <v-col cols="6" class="text-right font-weight-medium">
            {{ item.product_return.remark || "-" }}
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Notes Section -->
    <v-card outlined v-if="item.note" class="rounded-lg mb-4">
      <v-card-title class="text-subtitle-1 font-weight-bold">
        {{ $t("noted") }}
      </v-card-title>
      <v-card-text class="text-body-1">
        {{ item.note }}
      </v-card-text>
    </v-card>
    <!-- Ticket Verification Section - Only show if store has ticket verification enabled -->
    <v-card v-if="isTicketVerificationEnabled" outlined class="rounded-lg">
      <v-card-title class="text-subtitle-1 font-weight-bold">
        <v-icon
          small
          :color="getVerificationStatusColor(item.verification_status)"
          left
        >
          {{ getVerificationStatusIcon(item.verification_status) }}
        </v-icon>
        {{ $t("ticket_verification") }}
        <v-spacer />
        <v-chip
          small
          :color="getVerificationStatusColor(item.verification_status)"
          :text-color="
            getVerificationStatusColor(item.verification_status) === 'success'
              ? 'white'
              : 'white'
          "
          class="rounded-lg"
        >
          {{ $t(item.verification_status || "pending") }}
        </v-chip>
      </v-card-title>

      <v-card-text class="text-body-1">
        <!-- Show verification reason if already verified -->
        <div
          v-if="
            item.verification_status && item.verification_status !== 'pending'
          "
        >
          <div class="text-caption grey--text mb-2">
            {{ $t("verification_reason") }}:
          </div>
          <div class="text-body-2 mb-3">
            {{ item.verification_reason || $t("no_reason_provided") }}
          </div>
          <div class="text-caption grey--text">
            {{ $t("verified_at") }}: {{ item.verified_at | moment }}
          </div>
        </div>

        <!-- Verification form for pending tickets -->
        <div v-else>
          <v-textarea
            v-model="verificationReason"
            :label="$t('verification_reason')"
            :placeholder="$t('enter_reason_if_rejecting')"
            outlined
            rows="3"
            :rules="verificationRules"
            :error-messages="verificationErrors"
            @input="clearVerificationErrors"
          />

          <div class="d-flex justify-end mt-4">
            <v-btn
              color="success"
              class="mr-2"
              @click="handleApproval"
              :loading="isVerifying"
            >
              <v-icon left small>fal fa-check-double</v-icon>
              {{ $t("approved") }}
            </v-btn>
            <v-btn
              color="error"
              @click="handleRejection"
              :loading="isVerifying"
            >
              <v-icon left small>fal fa-times</v-icon>
              {{ $t("rejected") }}
            </v-btn>
          </div>
        </div>
      </v-card-text>
    </v-card>
  </v-card>
</template>

<script>
import { myMixin } from "@/mixin/main";
import TextArea from "@/components/global/TextArea";
import CancelHistory from "@/components/reports/CancelHistory";
import { toast, error } from "@/controllers/alert";
import { hasPermission } from "@/controllers/auth";
import { formatBillQueue } from "@/utils/billQueue";

export default {
  mixins: [myMixin],

  components: {
    TextArea,
    CancelHistory,
  },

  props: {
    item: {
      type: Object,
    },
  },

  data() {
    return {
      verificationReason: "",
      isVerifying: false,
      verificationErrors: [],
    };
  },

  computed: {
    hasCancellations() {
      return (this.item?.cancellations || []).length > 0;
    },
    queueLabel() {
      return formatBillQueue(this.item?.queue_no);
    },
    totalPrice() {
      if (Object.keys(this.item).length > 0) {
        const { total_discount, discount_type, total_price } = this.item;
        if (discount_type === "percentage") {
          var percent = (total_discount / 100) * total_price;
          return this.$options.filters.NumberFormat(percent);
        } else {
          const price = total_price - total_discount;
          return this.$options.filters.NumberFormat(price);
        }
      }
    },
    total_discount() {
      if (Object.keys(this.item).length > 0) {
        const { total_discount } = this.item;
        if (total_discount === 0) return this.$t("no_discount");
        return `- ${this.$options.filters.NumberFormat(total_discount)} ${
          this.productCurrency || "LAK"
        }`;
      }
    },
    grand_total() {
      if (Object.keys(this.item).length > 0) {
        const { total_price_sale } = this.item;
        return this.$options.filters.NumberFormat(total_price_sale);
      }
    },
    subtotal() {
      if (Object.keys(this.item).length > 0) {
        const { total_price } = this.item;
        return this.$options.filters.NumberFormat(total_price);
      }
    },
    // Check if tax should be displayed
    shouldShowTax() {
      if (!this.item || Object.keys(this.item).length === 0) return false;
      // Show tax if tax_amount exists and is greater than 0
      return this.item.tax_amount && this.item.tax_amount > 0;
    },
    // Format tax amount for display
    tax_amount_formatted() {
      if (this.item?.tax_amount) {
        return this.$options.filters.NumberFormat(this.item.tax_amount);
      }
      return "0.00";
    },
    // Get tax percentage from auth store
    taxPercentage() {
      return this.tax_rate || 10;
    },
    // Calculate grand total before payment (subtotal - discount + tax)
    grandTotalBeforePayment() {
      if (!this.item || Object.keys(this.item).length === 0) return "0.00";
      const subtotal =
        Number(this.item.total_price_sale || 0) -
        Number(this.item.total_discount || 0);
      const tax = Number(this.item.tax_amount || 0);
      const grandTotal = Math.max(subtotal, 0) + tax;
      return this.$options.filters.NumberFormat(grandTotal);
    },
    isCustomer() {
      if (this.item && this.item.customer_id) {
        return true;
      }
      return false;
    },
    // Payment currency - shows the currency the customer actually paid in
    paymentCurrency() {
      // Prefer ticket's currency_id from API (payment currency)
      if (this.item?.currency_id?.code && this.item?.total_paid != null) {
        return this.item.currency_id.code;
      }
      if (
        this.item &&
        this.item.total_paid &&
        this.item.total_price &&
        this.item.exchange_rate
      ) {
        const ratio = this.item.total_paid / this.item.total_price;
        const exchangeRate = this.item.exchange_rate;

        // Calculate expected ratios for different currencies
        const thbRatio = 1 / exchangeRate; // For THB payment
        const usdRatio = 1 / (exchangeRate * 0.027); // Approximate USD rate (1 USD ≈ 37 THB)

        // If ratio is very small (< 0.1), customer likely paid in higher value currency
        if (ratio < 0.1) {
          // Check if it matches THB conversion (within 10% tolerance)
          if (Math.abs(ratio - thbRatio) / thbRatio < 0.1) {
            return "THB";
          }
          // Check if it matches USD conversion (within 20% tolerance due to approximation)
          else if (Math.abs(ratio - usdRatio) / usdRatio < 0.2) {
            return "USD";
          }
        }
        // If ratio is close to 1, customer paid in same currency
        else if (ratio > 0.9 && ratio < 1.1) {
          return this.item.currency_id?.code || "LAK";
        }
        // If ratio is very large, customer likely paid in lower value currency (e.g. price THB, paid LAK)
        else if (ratio > 10) {
          return "LAK";
        }
      }
      return this.item?.currency_id?.code || "LAK";
    },

    // Product currency - gets the currency from the first product in history
    productCurrency() {
      if (this.item?.history && this.item.history.length > 0) {
        return this.item.history[0].product?.currency?.code || "LAK";
      }
      return this.item?.currency_id?.code || "LAK";
    },

    // Check if we should show payment currency (when different from product currency)
    showPaymentCurrency() {
      return (
        this.paymentCurrency !== this.productCurrency && this.item?.total_paid
      );
    },

    // True when price is in one currency (e.g. THB) and user paid in another (e.g. LAK) – show exchange rate and LAK section
    isPaymentInDifferentCurrency() {
      return (
        this.paymentCurrency !== this.productCurrency &&
        this.item?.total_paid != null &&
        Number(this.item?.exchange_rate) > 0
      );
    },

    // Formatted amount due in payment currency (e.g. 462,000 when 660 THB at 700)
    amountDueInPaymentCurrencyFormatted() {
      if (!this.isPaymentInDifferentCurrency) return "0";
      const amount = this.amountDueInPaymentCurrency;
      return this.$options.filters.NumberFormat(amount);
    },

    // Format the total paid amount
    total_paid_formatted() {
      if (this.item?.total_paid) {
        return this.$options.filters.NumberFormat(this.item.total_paid);
      }
      return "0.00";
    },

    /**
     * Amount due converted to payment currency (for comparison with total_paid).
     * When price is in THB and user paid in LAK: total_price is THB, exchange_rate
     * is LAK per 1 THB → amount due in LAK = total_price * exchange_rate.
     */
    amountDueInPaymentCurrency() {
      if (!this.item) return 0;
      const totalPrice = Number(this.item.total_price) || 0;
      const productCur = this.productCurrency;
      const paymentCur = this.paymentCurrency;
      if (productCur === paymentCur) return totalPrice;
      const rate = Number(this.item.exchange_rate) || 0;
      if (!rate) return totalPrice;
      // Price in THB (or USD), paid in LAK: exchange_rate = LAK per 1 unit of product currency
      // So amount due in LAK = total_price * exchange_rate (e.g. 660 THB * 700 = 462,000 LAK)
      return totalPrice * rate;
    },

    // Check if payment amount exactly matches total (no change due)
    isExactPayment() {
      if (!this.item) return false;
      const totalPaid = Number(this.item.total_paid) || 0;
      const due = this.amountDueInPaymentCurrency;
      // Allow tiny tolerance for floating point
      return Math.abs(totalPaid - due) < 1;
    },

    // Check if customer paid more than total (has change due)
    hasChange() {
      if (!this.item) return false;
      const totalPaid = Number(this.item.total_paid) || 0;
      const due = this.amountDueInPaymentCurrency;
      return totalPaid > due + 1;
    },

    // Calculate and format change amount (in payment currency)
    change_formatted() {
      if (!this.item) return "0.00";
      const totalPaid = Number(this.item.total_paid) || 0;
      const due = this.amountDueInPaymentCurrency;
      const change = Math.max(0, totalPaid - due);
      return this.$options.filters.NumberFormat(change);
    },

    // Check if ticket verification is enabled for this store
    isTicketVerificationEnabled() {
      return (
        this.user &&
        this.user.stores &&
        this.user.stores.ticket_verification === 1
      );
    },

    // Validation rules for verification reason
    verificationRules() {
      return [(v) => !v || v.length <= 500 || this.$t("reason_too_long")];
    },

    /**
     * Parsed cash / transfer amounts for tickets with type cash_transfer.
     */
    cashTransferBreakdown() {
      if (
        !this.item ||
        this.item.type !== "cash_transfer" ||
        this.item.payment_data == null ||
        this.item.payment_data === ""
      ) {
        return null;
      }
      return this.parseCashTransferPaymentData(this.item.payment_data);
    },

    receiptBuffetPlans() {
      if (!this.item || Object.keys(this.item).length === 0) {
        return [];
      }
      const r = this.item;
      if (Array.isArray(r.buffet_plans) && r.buffet_plans.length) {
        return r.buffet_plans;
      }
      if (Array.isArray(r.buffetPlans) && r.buffetPlans.length) {
        return r.buffetPlans;
      }
      return [];
    },
    hasBuffetPlans() {
      return this.receiptBuffetPlans.length > 0;
    },
    buffetTotalPersons() {
      return this.receiptBuffetPlans.reduce(
        (sum, p) => sum + (Number(p.persons) || 0),
        0,
      );
    },
  },

  methods: {
    /**
     * Read cash + transfer from payment_data JSON (outer snake_case or nested payment_details camelCase).
     */
    parseCashTransferPaymentData(raw) {
      try {
        const obj = typeof raw === "string" ? JSON.parse(raw) : raw;
        if (!obj || typeof obj !== "object") {
          return null;
        }

        const hasOuterCash =
          obj.cash_amount !== undefined &&
          obj.cash_amount !== null &&
          obj.cash_amount !== "";
        const hasOuterTransfer =
          obj.transfer_amount !== undefined &&
          obj.transfer_amount !== null &&
          obj.transfer_amount !== "";

        if (hasOuterCash || hasOuterTransfer) {
          const cash = Number(obj.cash_amount);
          const transfer = Number(obj.transfer_amount);
          return {
            cash: Number.isFinite(cash) ? cash : 0,
            transfer: Number.isFinite(transfer) ? transfer : 0,
          };
        }

        let details = obj.payment_details;
        if (details == null) {
          return null;
        }
        if (typeof details === "string") {
          details = JSON.parse(details);
        }
        if (!details || typeof details !== "object") {
          return null;
        }

        const cash = Number(details.cashAmount ?? details.cash_amount ?? 0);
        const transfer = Number(
          details.transferAmount ?? details.transfer_amount ?? 0,
        );
        return {
          cash: Number.isFinite(cash) ? cash : 0,
          transfer: Number.isFinite(transfer) ? transfer : 0,
        };
      } catch (e) {
        return null;
      }
    },

    // Check if user has a specific permission
    checkPermission(permissionCode) {
      return hasPermission(permissionCode);
    },
    showImg(val) {
      const path = Help.img(val);
      return path;
    },
    isStockTrackingEnabled(product) {
      // Check if the product has track_stock enabled
      // The track_stock setting might be in the product data or we can infer from stock values
      if (product.product && product.product.track_stock !== undefined) {
        return product.product.track_stock;
      }

      // If track_stock is not available, check if stock values are meaningful
      // If pre_stock and post_stock are both 0 or negative, likely stock tracking is disabled
      if (product.pre_stock === 0 && product.post_stock <= 0) {
        return false;
      }

      // Default to true if we can't determine
      return true;
    },
    getProductUnit(product) {
      // Get unit information from product
      if (product.product && product.product.size) {
        return product.product.size.name || this.$t("no_unit");
      }
      return this.$t("no_unit");
    },
    getProductSupplier(product) {
      // Get supplier information from product
      if (product.product && product.product.vendor) {
        const vendor = product.product.vendor;
        if (vendor.name && vendor.phone_no) {
          return `${vendor.name} - ${vendor.phone_no}`;
        } else if (vendor.name) {
          return vendor.name;
        } else if (vendor.phone_no) {
          return vendor.phone_no;
        }
      }
      return this.$t("no_supplier");
    },
    getProfitAmount(product) {
      // Since backend now stores original prices, no conversion needed
      const profit = (product.price_sale - product.price_buy) * product.qty;
      return profit;
    },
    getProfitColor(product) {
      const profit = this.getProfitAmount(product);
      return profit > 0 ? "success--text" : "error--text";
    },
    getVariantOptions(productVariant) {
      if (!productVariant) return {};

      // Try to get options from optionValues (object format)
      if (
        productVariant.optionValues &&
        typeof productVariant.optionValues === "object"
      ) {
        return productVariant.optionValues;
      }
      // Fallback to options array format
      else if (
        productVariant.options &&
        Array.isArray(productVariant.options)
      ) {
        const options = {};
        productVariant.options.forEach((option) => {
          if (option.productOption && option.productOption.storeOption) {
            options[option.productOption.storeOption.name] =
              option.selected_value;
          }
        });
        return options;
      }

      return {};
    },
    handleImageError(event) {
      // Handle image loading errors by setting a fallback
      event.target.src = "/images/no_photo.jpg"; // Fallback to a default image
    },

    // Get verification status color
    getVerificationStatusColor(status) {
      switch (status) {
        case "approved":
          return "success";
        case "rejected":
          return "error";
        default:
          return "warning";
      }
    },

    // Get verification status icon
    getVerificationStatusIcon(status) {
      switch (status) {
        case "approved":
          return "fal fa-check-circle";
        case "rejected":
          return "fal fa-times-circle";
        default:
          return "fal fa-clock";
      }
    },

    // Clear verification errors
    clearVerificationErrors() {
      this.verificationErrors = [];
    },

    // Handle ticket approval
    async handleApproval() {
      await this.verifyTicket("approved");
    },

    // Handle ticket rejection
    async handleRejection() {
      // Validate reason is provided for rejection
      if (!this.verificationReason || this.verificationReason.trim() === "") {
        this.verificationErrors = [this.$t("reason_required_for_rejection")];
        return;
      }

      await this.verifyTicket("rejected");
    },

    // Verify ticket with status and reason
    async verifyTicket(status) {
      this.isVerifying = true;
      this.clearVerificationErrors();

      try {
        const payload = {
          ticket_id: this.item.id,
          verification_status: status,
          reason: this.verificationReason || null,
        };

        const { data } = await this.$http.post("verify_ticket", payload);

        if (data.status) {
          // Emit event to parent component
          this.$emit("ticket-verified", {
            ticketId: this.item.id,
            status: status,
            reason: this.verificationReason,
            verifiedAt: new Date().toISOString(),
          });

          // Update local item data
          this.item.verification_status = status;
          this.item.verification_reason = this.verificationReason;
          this.item.verified_at = new Date().toISOString();

          // Show success message
          toast(this, "ticket_verification_success");
        }
      } catch (err) {
        console.error("Error verifying ticket:", err);
        error(this, "ticket_verification_failed");
      } finally {
        this.isVerifying = false;
      }
    },
  },
};
</script>

<style scoped>
.bill-detail {
  --bill-accent: var(--v-primary-base, #7a6bd0);
  --bill-accent-soft: rgba(122, 107, 208, 0.12);
  --bill-border: #e3e4ec;
  --bill-surface-soft: #f1f2f7;
  --bill-muted: #7d8190;
  --bill-text: #1b1c26;
  color: var(--bill-text);
}

::v-deep(.theme--dark) .bill-detail {
  --bill-text: #ffffff;
}

::v-deep(.theme--dark) .bill-detail .v-expansion-panel-header {
  color: #ffffff;
}

.bill-detail ::v-deep .v-card {
  border-radius: 8px !important;
  box-shadow: none !important;
}

.bill-detail > .v-row:first-child {
  margin-bottom: 2px;
}

.bill-detail > .v-row:first-child > .v-col {
  padding-top: 0;
}

.bill-detail > .v-row:first-child > .v-col > .v-card,
.bill-detail > .v-card {
  border-color: var(--bill-border) !important;
  border-radius: 8px !important;
  box-shadow: none !important;
}

.bill-detail ::v-deep .v-card__title {
  min-height: 42px;
  padding: 12px 14px 8px;
  color: var(--bill-text);
  font-size: 14px !important;
  font-weight: 500 !important;
}

.bill-detail ::v-deep .v-card__title .v-icon {
  color: var(--bill-accent) !important;
}

.bill-detail ::v-deep .v-card__text {
  padding: 12px 14px 14px;
}

.bill-detail ::v-deep .v-divider {
  border-color: var(--bill-border) !important;
}

/* Compact metadata and payment rows. */
.bill-detail > .v-row:first-child ::v-deep .v-card__title {
  border-bottom: 1px solid var(--bill-border);
}

.bill-detail > .v-row:first-child ::v-deep .v-row {
  row-gap: 2px;
}

.bill-detail > .v-row:first-child ::v-deep .text-caption {
  color: var(--bill-muted) !important;
  font-size: 10.5px !important;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.bill-detail > .v-row:first-child ::v-deep .text-right {
  color: var(--bill-text);
  font-size: 12.5px;
  font-variant-numeric: tabular-nums;
}

/* The reference uses a soft surface for totals and item rows. */
.bill-detail > .v-row:first-child > .v-col:nth-child(2) > .v-card {
  background: var(--bill-surface-soft) !important;
}

.bill-detail > .v-row:first-child > .v-col:nth-child(2) ::v-deep .v-card__title {
  background: transparent;
}

.bill-detail > .v-row:first-child ::v-deep .text-subtitle-1,
.bill-detail > .v-row:first-child ::v-deep .text-subtitle-2 {
  color: var(--bill-text) !important;
}

.bill-detail > .v-row:first-child ::v-deep .primary--text,
.bill-detail > .v-row:first-child ::v-deep .success--text {
  color: var(--bill-accent) !important;
}

/* Product section heading and expanded line items. */
.bill-detail-products ::v-deep .v-card__title {
  padding-bottom: 10px;
}

.bill-detail-products ::v-deep .v-expansion-panels {
  padding: 0 12px 12px;
}

.bill-detail ::v-deep .v-expansion-panel {
  margin-bottom: 9px !important;
  overflow: hidden;
  border: 1px solid var(--bill-border) !important;
  border-radius: 8px !important;
  background: var(--bill-surface-soft) !important;
}

.bill-detail ::v-deep .v-expansion-panel::before,
.bill-detail ::v-deep .v-expansion-panel:after {
  display: none;
}

.bill-detail ::v-deep .v-expansion-panel-header {
  min-height: 64px;
  padding: 10px 12px;
  font-size: 12.5px;
}

.bill-detail ::v-deep .v-expansion-panel-header__icon {
  margin-left: 8px;
}

.bill-detail ::v-deep .v-expansion-panel-content__wrap {
  padding: 0 12px 14px;
}

.bill-detail ::v-deep .v-expansion-panel-content {
  border-top: 1px solid var(--bill-border);
}

.bill-detail ::v-deep .v-expansion-panel-content .text-caption,
.bill-detail ::v-deep .v-expansion-panel-content .grey--text {
  color: var(--bill-muted) !important;
}

.bill-detail ::v-deep .v-expansion-panel-content .text-subtitle-2 {
  color: var(--bill-text);
  font-size: 12.5px !important;
}

.bill-detail ::v-deep .v-chip {
  border-radius: 999px !important;
  font-size: 10.5px;
}

@media (max-width: 600px) {
  .bill-detail ::v-deep .v-card__title {
    padding-left: 11px;
    padding-right: 11px;
  }

  .bill-detail ::v-deep .v-expansion-panel-header {
    min-height: 76px;
  }
}
</style>
